import { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Badge,
  Button,
  Checkbox,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  CheckmarkCircleRegular,
  ClockRegular,
  AddRegular,
  DeleteRegular,
  DocumentPdfRegular,
  FolderZipRegular,
  PenRegular,
  WarningRegular,
  MailRegular,
} from '@fluentui/react-icons';
import { format, differenceInDays, parseISO } from 'date-fns';
import {
  documentTypeDisplayNames,
  signatureDocumentTypes,
  appointmentTypeDisplayNames,
  isVirtualAppointment,
  type TaxReturn,
  type TaxDocument,
  type AppointmentType,
} from '../../types';
import { downloadTaxDocumentsZip } from '../../services/api';
import DataTable, { type Column } from '../../components/DataTable';
import DocumentChecklist from '../../components/DocumentChecklist';
import type { UseMutationResult } from '@tanstack/react-query';

// Document categories for separate sections. Signature types live in types/index.ts
// (signatureDocumentTypes) so the Send-for-Signature dropdown stays in sync.
const supportDocTypes = ['w2', '1099_misc', '1099_nec', '1099_int', '1099_div', '1099_b', '1099_r', 'k1', '1098', '1098_t', 'property_tax', 'charitable_donations', 'medical_expenses', 'business_expenses', 'prior_year_return', 'id_verification', 'other'];
const finalDocTypes = ['completed_return'];

export interface DocumentsTabProps {
  taxReturn: TaxReturn;
  documents: TaxDocument[];
  deleteDocMutation: UseMutationResult<unknown, Error, string>;
  onUploadDialogOpen: (docType: string) => void;
  onSendDocumentReminder?: () => void;
  documentReminderPending?: boolean;
}

export function DocumentsTab({
  taxReturn,
  documents,
  deleteDocMutation,
  onUploadDialogOpen,
  onSendDocumentReminder,
  documentReminderPending,
}: DocumentsTabProps) {
  const { t } = useTranslation();

  const supportDocs = documents.filter(d => supportDocTypes.includes(d.documentType));
  const signatureDocs = documents.filter(d => signatureDocumentTypes.includes(d.documentType));
  const finalDocs = documents.filter(d => finalDocTypes.includes(d.documentType));

  // Multi-select state for bulk ZIP download on the Support Documents table
  const [selectedSupportIds, setSelectedSupportIds] = useState<Set<string>>(new Set());
  const [lastSupportClickedId, setLastSupportClickedId] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);

  // Detect filenames that appear more than once in the support docs list
  // so we can warn the user that they'll be auto-renamed in the downloaded zip.
  const duplicateFileNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of supportDocs) counts.set(d.fileName, (counts.get(d.fileName) ?? 0) + 1);
    return new Set(Array.from(counts.entries()).filter(([, n]) => n > 1).map(([name]) => name));
  }, [supportDocs]);

  const toggleSupportSelected = (id: string, shiftKey: boolean) => {
    setSelectedSupportIds(prev => {
      const next = new Set(prev);
      // Shift-click range toggle across the visible support docs
      if (shiftKey && lastSupportClickedId) {
        const startIdx = supportDocs.findIndex(d => d.id === lastSupportClickedId);
        const endIdx = supportDocs.findIndex(d => d.id === id);
        if (startIdx !== -1 && endIdx !== -1) {
          const [lo, hi] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
          const shouldSelect = !next.has(id);
          for (let i = lo; i <= hi; i++) {
            if (shouldSelect) next.add(supportDocs[i].id);
            else next.delete(supportDocs[i].id);
          }
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setLastSupportClickedId(id);
  };

  const toggleSupportSelectAll = () => {
    const allSelected = supportDocs.length > 0 && supportDocs.every(d => selectedSupportIds.has(d.id));
    setSelectedSupportIds(prev => {
      const next = new Set(prev);
      if (allSelected) supportDocs.forEach(d => next.delete(d.id));
      else supportDocs.forEach(d => next.add(d.id));
      return next;
    });
  };

  const handleDownloadSupportZip = async () => {
    const selected = supportDocs.filter(d => selectedSupportIds.has(d.id));
    if (selected.length === 0) return;
    setZipping(true);
    try {
      const blob = await downloadTaxDocumentsZip(
        selected.map(d => ({ id: d.id, taxReturnId: d.taxReturnId })),
      );
      if (blob.size === 0) throw new Error(t('documentsTab.supportDocuments.emptyZipError'));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientSlug = (taxReturn.clientName || 'tax-return').replace(/[^\w-]/g, '_');
      const year = taxReturn.taxYear ? `-${taxReturn.taxYear}` : '';
      a.download = `${clientSlug}${year}-documents-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert(t('documentsTab.supportDocuments.zipFailedAlert', {
        message: err instanceof Error ? err.message : t('documentsTab.supportDocuments.unknownError'),
      }));
    } finally {
      setZipping(false);
    }
  };

  // Calculate days until appointment for virtual warning
  const daysUntilAppointment = taxReturn.scheduledDate
    ? differenceInDays(parseISO(taxReturn.scheduledDate), new Date())
    : null;

  // Show warning for virtual appointments when docs not received and appointment is approaching
  const showVirtualDocWarning = isVirtualAppointment(taxReturn.appointmentType)
    && !taxReturn.allDocumentsReceived
    && daysUntilAppointment !== null
    && daysUntilAppointment >= 0
    && daysUntilAppointment <= 7;

  return (
    <>
      {/* Virtual Appointment Document Warning */}
      {showVirtualDocWarning && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#fff4ce',
          borderLeft: '4px solid #f59e0b',
          borderRadius: '4px',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <WarningRegular style={{ color: '#f59e0b', fontSize: tokens.fontSizeBase600, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '4px', color: '#92400e' }}>
                {t('documentsTab.virtualWarning.heading')}
              </div>
              <div style={{ fontSize: tokens.fontSizeBase300, color: '#78350f', marginBottom: '8px' }}>
                <Trans
                  i18nKey="documentsTab.virtualWarning.body"
                  values={{
                    timing: daysUntilAppointment === 0
                      ? t('documentsTab.virtualWarning.today')
                      : t('documentsTab.virtualWarning.inDays', { count: daysUntilAppointment }),
                  }}
                  components={{ strong: <strong /> }}
                />
              </div>
              {taxReturn.missingDocuments && taxReturn.missingDocuments.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#92400e' }}>{t('documentsTab.virtualWarning.missingDocumentsLabel')}</span>
                  <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                    {taxReturn.missingDocuments.map((doc, idx) => (
                      <li key={idx} style={{ color: '#78350f', fontSize: '13px' }}>
                        {documentTypeDisplayNames[doc] || doc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {onSendDocumentReminder && (
                <Button
                  appearance="primary"
                  icon={<MailRegular />}
                  size="small"
                  onClick={onSendDocumentReminder}
                  disabled={documentReminderPending}
                >
                  {documentReminderPending ? t('documentsTab.virtualWarning.sendingReminder') : t('documentsTab.virtualWarning.sendReminder')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Virtual/Drop-off appointment type indicator (non-warning, always visible) */}
      {(isVirtualAppointment(taxReturn.appointmentType) || taxReturn.appointmentType === 'drop-off') && !showVirtualDocWarning && (
        <MessageBar
          intent={taxReturn.allDocumentsReceived ? 'success' : 'info'}
          style={{ marginBottom: '24px' }}
        >
          <MessageBarBody>
            <MessageBarTitle>
              {t('documentsTab.appointmentIndicator.title', {
                appointmentType: appointmentTypeDisplayNames[taxReturn.appointmentType as AppointmentType],
              })}
            </MessageBarTitle>
            {taxReturn.allDocumentsReceived
              ? t('documentsTab.appointmentIndicator.allReceived')
              : isVirtualAppointment(taxReturn.appointmentType)
                ? t('documentsTab.appointmentIndicator.virtualPending')
                : t('documentsTab.appointmentIndicator.dropoffPending')}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Document Status */}
      <div className="detail-section" style={{ marginBottom: '24px' }}>
        <h3><DocumentRegular /> {t('documentsTab.documentStatus.heading')}</h3>
        <div className="detail-row">
          <span className="detail-label">{t('documentsTab.common.status')}</span>
          <span className="detail-value">
            {taxReturn.allDocumentsReceived ? (
              <Badge appearance="filled" color="success">
                <CheckmarkCircleRegular /> {t('documentsTab.documentStatus.allReceivedBadge')}
              </Badge>
            ) : (
              <Badge appearance="filled" color="warning">
                <ClockRegular /> {t('documentsTab.documentStatus.pendingBadge')}
              </Badge>
            )}
          </span>
        </div>
        {taxReturn.documentsReceivedDate && (
          <div className="detail-row">
            <span className="detail-label">{t('documentsTab.documentStatus.receivedDateLabel')}</span>
            <span className="detail-value">
              {format(new Date(taxReturn.documentsReceivedDate), 'MMM d, yyyy')}
            </span>
          </div>
        )}
        {taxReturn.missingDocuments && taxReturn.missingDocuments.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <span className="detail-label">{t('documentsTab.documentStatus.missingDocumentsLabel')}</span>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              {taxReturn.missingDocuments.map((doc, idx) => (
                <li key={idx} style={{ color: '#d83b01', fontSize: tokens.fontSizeBase300 }}>
                  {documentTypeDisplayNames[doc] || doc}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Document Checklist - Drake-based */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <span className="card-title">
            <DocumentRegular style={{ marginRight: '8px' }} />
            {t('documentsTab.checklist.heading')}
          </span>
        </div>
        <div style={{ padding: '16px' }}>
          <DocumentChecklist
            clientId={taxReturn.clientId}
            taxYear={taxReturn.taxYear}
            variant="full"
            showProgress
            editable
          />
        </div>
      </div>

      {/* Support Documents */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <span className="card-title">
            <DocumentRegular style={{ marginRight: '8px' }} />
            {t('documentsTab.supportDocuments.heading', { count: supportDocs.length })}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              appearance="secondary"
              icon={<FolderZipRegular />}
              disabled={selectedSupportIds.size === 0 || zipping}
              onClick={handleDownloadSupportZip}
            >
              {zipping ? <Spinner size="tiny" /> : selectedSupportIds.size > 0
                ? t('documentsTab.supportDocuments.downloadZipWithCount', { count: selectedSupportIds.size })
                : t('documentsTab.supportDocuments.downloadZip')}
            </Button>
            <Button
              appearance="primary"
              icon={<AddRegular />}
              onClick={() => onUploadDialogOpen('other')}
            >
              {t('documentsTab.supportDocuments.uploadDocument')}
            </Button>
          </div>
        </div>
        {supportDocs.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--tl-text-secondary)' }}>
            {t('documentsTab.supportDocuments.emptyMessage')}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '40px' }}>
                  <Checkbox
                    checked={supportDocs.length > 0 && supportDocs.every(d => selectedSupportIds.has(d.id))}
                    onChange={toggleSupportSelectAll}
                  />
                </TableHeaderCell>
                <TableHeaderCell>{t('documentsTab.common.fileName')}</TableHeaderCell>
                <TableHeaderCell>{t('documentsTab.common.type')}</TableHeaderCell>
                <TableHeaderCell>{t('documentsTab.common.uploaded')}</TableHeaderCell>
                <TableHeaderCell>{t('documentsTab.common.size')}</TableHeaderCell>
                <TableHeaderCell>{t('documentsTab.common.actions')}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {supportDocs.map(doc => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedSupportIds.has(doc.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSupportSelected(doc.id, e.shiftKey);
                      }}
                      onChange={() => { /* handled in onClick to capture shiftKey */ }}
                    />
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <DocumentRegular />
                      <span>{doc.fileName}</span>
                      {duplicateFileNames.has(doc.fileName) && (
                        <Tooltip
                          content={t('documentsTab.supportDocuments.duplicateNameTooltip')}
                          relationship="label"
                        >
                          <Badge appearance="tint" color="warning" size="small">{t('documentsTab.supportDocuments.duplicateNameBadge')}</Badge>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{documentTypeDisplayNames[doc.documentType] || doc.documentType}</TableCell>
                  <TableCell>{format(new Date(doc.uploadedAt), 'MMM d, yyyy')}</TableCell>
                  <TableCell>{`${(doc.fileSize / 1024).toFixed(1)} KB`}</TableCell>
                  <TableCell>
                    <div className="action-buttons">
                      <Button appearance="subtle" as="a" href={doc.blobUrl} target="_blank">
                        {t('documentsTab.common.view')}
                      </Button>
                      <Button
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteDocMutation.mutate(doc.id);
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Signature Documents */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <span className="card-title">
            <PenRegular style={{ marginRight: '8px' }} />
            {t('documentsTab.signatureDocuments.heading', { count: signatureDocs.length })}
          </span>
          <Button
            appearance="secondary"
            icon={<AddRegular />}
            onClick={() => onUploadDialogOpen('unsigned_8879')}
          >
            {t('documentsTab.signatureDocuments.uploadUnsigned8879')}
          </Button>
        </div>
        <DataTable
          data={signatureDocs}
          rowKey="id"
          columns={[
            {
              key: 'fileName',
              header: t('documentsTab.common.fileName'),
              sortable: true,
              render: (doc) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DocumentRegular />
                  <span>{doc.fileName}</span>
                </div>
              ),
            },
            {
              key: 'documentType',
              header: t('documentsTab.common.type'),
              sortable: true,
              render: (doc) => documentTypeDisplayNames[doc.documentType] || doc.documentType,
            },
            {
              key: 'uploadedAt',
              header: t('documentsTab.common.uploaded'),
              sortable: true,
              getValue: (doc) => new Date(doc.uploadedAt).getTime(),
              render: (doc) => format(new Date(doc.uploadedAt), 'MMM d, yyyy'),
            },
            {
              key: 'actions',
              header: t('documentsTab.common.actions'),
              render: (doc) => (
                <div className="action-buttons">
                  <Button appearance="subtle" as="a" href={doc.blobUrl} target="_blank">
                    {t('documentsTab.common.view')}
                  </Button>
                  <Button
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteDocMutation.mutate(doc.id);
                    }}
                  />
                </div>
              ),
            },
          ] as Column<TaxDocument>[]}
          emptyMessage={t('documentsTab.signatureDocuments.emptyMessage')}
        />
      </div>

      {/* Final Return Documents */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div className="card-header">
          <span className="card-title">
            <DocumentPdfRegular style={{ marginRight: '8px' }} />
            {t('documentsTab.finalDocuments.heading', { count: finalDocs.length })}
          </span>
          <Button
            appearance="secondary"
            icon={<AddRegular />}
            onClick={() => onUploadDialogOpen('completed_return')}
          >
            {t('documentsTab.finalDocuments.uploadFinalReturn')}
          </Button>
        </div>
        <DataTable
          data={finalDocs}
          rowKey="id"
          columns={[
            {
              key: 'fileName',
              header: t('documentsTab.common.fileName'),
              sortable: true,
              render: (doc) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DocumentPdfRegular />
                  <span>{doc.fileName}</span>
                </div>
              ),
            },
            {
              key: 'uploadedAt',
              header: t('documentsTab.common.uploaded'),
              sortable: true,
              getValue: (doc) => new Date(doc.uploadedAt).getTime(),
              render: (doc) => format(new Date(doc.uploadedAt), 'MMM d, yyyy'),
            },
            {
              key: 'actions',
              header: t('documentsTab.common.actions'),
              render: (doc) => (
                <div className="action-buttons">
                  <Button appearance="subtle" as="a" href={doc.blobUrl} target="_blank">
                    {t('documentsTab.common.view')}
                  </Button>
                  <Button
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteDocMutation.mutate(doc.id);
                    }}
                  />
                </div>
              ),
            },
          ] as Column<TaxDocument>[]}
          emptyMessage={t('documentsTab.finalDocuments.emptyMessage')}
        />
      </div>
    </>
  );
}
